<script>
import CoinBit from "./helpers/CoinBit.vue";
import ToolTip from "./helpers/ToolTip.vue";

export default {
  name: "CoinGraph",

  components: {
    CoinBit,
    ToolTip,
  },

  props: {
    stackMax: {
      type: Number,
      default: 12,
      required: false
    },

    investmentEarnings: {
      type: Number,
      default: 0,
      required: false
    },

    bondContributions: {
      type: Number,
      default: 0,
      required: false
    },

    managedFund: {
      type: Number,
      default: 0,
      required: false
    },

    termDeposit: {
      type: Number,
      default: 0,
      required: false
    },

    bondLabel: {
      type: String,
      default: 'Your bond',
      required: false
    },
    
    cssClass: {
      type: String,
      default: '',
      required: false
    },

    capitalGainManagedFund: {
      type: Number,
      default: 0,
      required: false
    },
  },

  computed: {
    calculationDivisor() {
      return Math.max(this.bondContributions, Math.max(this.managedFund, this.termDeposit)) / this.stackMax; // Use whichever stack is the largest
    },

    investmentEarningsGraphStack() {
      if (this.investmentEarnings > 0) {
        return Math.max(1, Math.floor(this.investmentEarnings / this.calculationDivisor));
      }
      return 0;
    },

    bondContributionsGraphStack() {
      if (this.bondContributions > 0) {
        return Math.max(1, Math.floor((this.bondContributions - this.investmentEarnings) / this.calculationDivisor));
      }
      return 0;
    },

    managedFundGraphStack() {
      if (this.managedFund > 0) {
        return Math.max(1, Math.floor(this.managedFund / this.calculationDivisor));
      }
      return 0;
    },

    termDepositGraphStack() {
      if (this.termDeposit > 0) {
        return Math.max(1, Math.floor(this.termDeposit / this.calculationDivisor));
      }
      return 0;
    },

    formattedTooltip() {
      const tip = `
      <div class="tt">
        <div>
          <span class="label">
            Contributions:
          </span> 
          <span class="value">
            $${
              this.$formatCurrency(this.bondContributions - this.investmentEarnings)
            }
          </span>
        </div>
        <div>
          <span class="label">
            Earnings:
          </span> 
          <span class="value">
            $${
              this.$formatCurrency(this.investmentEarnings)
            }
          </span>
        </div>
        <div class="tttotal">
          <span class="label">
            Total:
          </span> 
          <span class="value">
            $${
              this.$formatCurrency(this.bondContributions)
            }
          </span>
        </div>
      </div>`;

      return tip;
    }
  },
};

/*
  LOGIC:

  If I get the following inputs from the engine output..

  * Investment Earnings
  * Bond Contributions
  * Managed Fund
  * Term Deposit

  So then to get a coin graph with 12 segments, we need to make these numbers into fractions of 12.

  To do that, i'm thinking..

  Bond Contributions (includes Investment Earnings) or Managed Fund or Term Deposit
  Find the biggest number out of the 3;
  then divide that by 12;
  use this number as the divisor for all numbers (floored but minimum of 1), to give you a fraction of 12 for the graph display.

  e.g.

  * Bond Contributions (Investment Earnings) = 6400
  * Bond Contributions (Regular Deposits) = 1136400
  * Managed Fund = 836400
  * Term Deposit = 436400

  Divisor = 1136400 / 12 = 95233

  =

  * Bond Contributions (Investment Earnings) = 6400 = floor(0.067) = 1
  * Bond Contributions (Regular Deposits) = 1136400 = floor(11.932) = 11
  * Managed Fund = 836400 = floor(8.782) = 8
  * Term Deposit = 436400 = floor(4.582) = 4
*/
</script>

<template>
  <div id="coinGraph">

    <!-- <div class="row mt-3 mb-5 wrapper">
      <div class="col-6 col-md-4 text-center ps-sm-5 ps-md-0 ps-xl-5 ms-md-auto">
        <span class="legend-conts">Contributions made</span>
      </div>
      <div class="col-6 col-md-4 text-center pe-sm-5 pe-md-0 pe-xl-5 me-md-auto">
        <span class="legend-invs">Investment earnings</span>
      </div>
    </div> -->

    <div class="d-flex align-items-stretch graph-limit mx-auto coin-wrapper">

      <!-- Investment Bond -->
      <div class="d-flex justify-content-center flex-basis-33">
        <div class="d-flex flex-column align-self-end">
          <div class="coin-graph investment-coin-graph d-flex flex-column align-self-center">
            <!-- investment-earnings / bond-contributions -->
            <div class="z" v-if="investmentEarningsGraphStack > 0"><!-- Need parent div around tooltips -->
              <ToolTip 
                :text="formattedTooltip"
                cssClass="mx-0 d-inline" 
                :options="{ placement: 'right' }"
              >
                <CoinBit 
                  part="top" 
                  cssClass="investment-earnings" 
                />
                <CoinBit 
                  v-if="investmentEarningsGraphStack > 1"
                  part="side" 
                  cssClass="investment-earnings" 
                  :amount="investmentEarningsGraphStack - 1" 
                />
              </ToolTip>
            </div>

            <CoinBit 
              v-if="investmentEarningsGraphStack === 0 && bondContributionsGraphStack > 0"
              part="top" 
              cssClass="bond-contributions"
            />
            <CoinBit 
              v-if="investmentEarningsGraphStack === 0 && bondContributionsGraphStack > 0"
              part="side" 
              cssClass="bond-contributions" 
              :amount="bondContributionsGraphStack - 1"
            />
            <CoinBit 
              v-else-if="bondContributionsGraphStack > 0"
              part="side" 
              cssClass="bond-contributions" 
              :amount="bondContributionsGraphStack" 
            />
          </div>

          <div class="d-flex flex-column align-self-center text-center pt-2 px-2 graph-label">            
            {{ this.bondLabel }}
            <div>
              <h4>${{ $formatCurrency(this.bondContributions) }}</h4>
            </div>
          </div>
          <small v-if="this.capitalGainManagedFund > 0" >No Capital Gains Tax</small>
        </div>
      </div>

      <!-- Manged Fund -->
      <div class="d-flex justify-content-center flex-basis-33">
        <div class="d-flex flex-column align-self-end">
          <div class="coin-graph managed-fund-coin-graph d-flex flex-column align-self-center">
            <CoinBit 
              v-if="managedFundGraphStack > 0"
              part="top" 
            />
            <CoinBit 
              v-if="managedFundGraphStack > 1"
              part="side" 
              :amount="managedFundGraphStack - 1" 
            />
          </div>

          <div class="d-flex flex-column align-self-center text-center pt-2 px-2 graph-label">            
            Managed fund
            <div>
              <h4>${{ $formatCurrency(this.managedFund) }}</h4>
            </div>
          </div>
          <small v-if="this.capitalGainManagedFund > 0" >Subject to <span class="red">${{$formatCurrency(this.capitalGainManagedFund)}}</span><br />Capital Gains Tax</small>
        </div>
      </div>

      <!-- Term Deposit -->
      <div class="d-flex justify-content-center flex-basis-33">
        <div class="d-flex flex-column align-self-end">
          <div class="coin-graph term-deposit-coin-graph d-flex flex-column align-self-center">
            <CoinBit 
              v-if="termDepositGraphStack > 0"
              part="top" 
            />
            <CoinBit 
              v-if="termDepositGraphStack > 1"
              part="side" 
              :amount="termDepositGraphStack - 1" 
            />
          </div>

          <div class="d-flex flex-column align-self-center text-center pt-2 px-2 graph-label">            
            Term deposit
            <div>
              <h4>${{ $formatCurrency(this.termDeposit) }}</h4>
            </div>
          </div>
          <small v-if="this.capitalGainManagedFund > 0">&nbsp;</small>
        </div>
      </div>

    </div>    
  </div>
</template>

<style lang="scss">
.z {
  z-index: 1;
}

.flex-basis-33 {
  flex-grow: 1 !important;
  flex-shrink: 1 !important;
  flex-basis: 33.33% !important;
}

.graph-limit {
  max-width: 740px;
  @media (max-width: 450px) {
    transform: scale(0.9);
  }
}

small {
  min-height: 40px;
  // text-align: center;
  span.red{
    color: #97242E;
    font-weight: bold;
  }
}

// Maybe move this to the main css?
#coinGraph {
  min-width: 330px;

  .coin-wrapper {
    min-height: 260px;
    display: flex;
    align-content: end;
    margin-bottom: 1rem !important;
  }

  .graph-label { 
    min-height: 66px;
    @media (max-width: 640px) {
      min-height: 82px;
    }

    font-size: 0.75rem;
    line-height: 0.9rem;
    text-transform: uppercase;
  }

  .coin-graph {
    width: 60px;

    small{
      text-align: center;
    }
  }
  

  .investment-coin-graph {
    scale: 1.3;
    margin-bottom: 20px;

    .investment-earnings {
      .coin-top,
      .coin-side {
        fill: #47B383;
      }
    }

    .bond-contributions {
      
      .coin-top,
      .coin-side {
        fill: #2b2b2b;
      }
    }
  }

  .managed-fund-coin-graph {
    .coin-top,
    .coin-side {
      fill: #63666a;
    }
  }

  .term-deposit-coin-graph {
    .coin-top,
    .coin-side {
      fill: #63666a;
    }
  }

  .stack-top {
    margin-bottom: 3px; /* at 90px wide */
  }

  .stack-side {
    margin-top: -15px; /* at 90px wide */
  }

  .legend-conts,
  .legend-invs {
    font-size: 0.85rem;
    display: inline-block;

    &::before {
      content: "";
      transform: translateY(0.22em);
      display: inline-block;
      box-sizing: border-box;
      margin: 0 5px 0 0;
      width: 1.15em;
      height: 1.15em;
      border: 0.2em solid #fff;
      border-radius: 50%;
      box-shadow: inset 1em 1em #47B383, 1px 1px 6px #aaaaaa;
    }
  }

  .legend-conts::before {
    box-shadow: inset 1em 1em #63666a, 1px 1px 6px #aaaaaa;
  }
  svg {
    outline: none;
  }
}

.tt {
  padding: 0.5rem;

  .label {
    display: inline-block;
    min-width: 110px;
    padding: 2px 0;
  }
  
  .value {
    font-weight: 700;
    padding: 2px 0;
  }

  .tttotal {
    border-top: 1px dashed #999;
    margin-top: 3px;
    padding-top: 3px;
  }
}
</style>